import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import InvoiceModal from '../components/InvoiceModal';
import InvoicePreview from '../components/InvoicePreview';
import { useInvoices } from '../hooks/useInvoices';

const { 
  FiPlus, FiSearch, FiEdit2, FiTrash2, FiEye, FiSend, FiDownload,
  FiRefreshCw, FiFileText, FiDollarSign, FiCalendar, FiFilter,
  FiGrid, FiList, FiArrowUp, FiArrowDown
} = FiIcons;

const Invoices = () => {
  const { invoices, loading, error, createInvoice, updateInvoice, deleteInvoice, updateInvoiceStatus, refetch } = useInvoices();
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [sortBy, setSortBy] = useState('issue_date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Filter and search invoices
  const filteredInvoices = invoices
    .filter(invoice => {
      const matchesSearch = 
        invoice.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.client?.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.client?.name?.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (filterBy === 'all') return matchesSearch;
      if (filterBy === 'draft') return matchesSearch && invoice.status === 'draft';
      if (filterBy === 'sent') return matchesSearch && invoice.status === 'sent';
      if (filterBy === 'paid') return matchesSearch && invoice.status === 'paid';
      if (filterBy === 'overdue') return matchesSearch && invoice.status === 'overdue';
      if (filterBy === 'recurring') return matchesSearch && invoice.invoice_type === 'recurring';
      if (filterBy === 'one_time') return matchesSearch && invoice.invoice_type === 'one_time';
      if (filterBy === 'ad_spend') return matchesSearch && invoice.invoice_type === 'ad_spend';
      
      return matchesSearch;
    })
    .sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'invoice_number':
          aValue = a.invoice_number || '';
          bValue = b.invoice_number || '';
          break;
        case 'client':
          aValue = a.client?.business_name || '';
          bValue = b.client?.business_name || '';
          break;
        case 'total_amount':
          aValue = a.total_amount || 0;
          bValue = b.total_amount || 0;
          break;
        case 'issue_date':
          aValue = new Date(a.issue_date || 0);
          bValue = new Date(b.issue_date || 0);
          break;
        case 'due_date':
          aValue = new Date(a.due_date || 0);
          bValue = new Date(b.due_date || 0);
          break;
        default:
          aValue = new Date(a.issue_date || 0);
          bValue = new Date(b.issue_date || 0);
      }
      
      if (typeof aValue === 'string') {
        return sortOrder === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    });

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowPreview(true);
  };

  const handleEditInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowModal(true);
  };

  const handleDeleteInvoice = async (id) => {
    if (window.confirm('Are you sure you want to delete this invoice? This action cannot be undone.')) {
      const result = await deleteInvoice(id);
      if (result.success) {
        // Success feedback could be added here
      }
    }
  };

  const handleCreateInvoice = async (invoiceData) => {
    const result = await createInvoice(invoiceData);
    if (result.success) {
      setShowModal(false);
      setSelectedInvoice(null);
    }
    return result;
  };

  const handleUpdateInvoice = async (invoiceData) => {
    if (selectedInvoice) {
      const result = await updateInvoice(selectedInvoice.id, invoiceData);
      if (result.success) {
        setShowModal(false);
        setSelectedInvoice(null);
      }
      return result;
    }
  };

  const handleStatusChange = async (invoiceId, newStatus) => {
    await updateInvoiceStatus(invoiceId, newStatus);
  };

  const formatCurrency = (amount) => {
    if (typeof amount === 'number') {
      return `$${amount.toLocaleString()}`;
    }
    return amount || '$0';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800 border-green-200';
      case 'sent': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'overdue': return 'bg-red-100 text-red-800 border-red-200';
      case 'draft': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'recurring': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'one_time': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'ad_spend': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Calculate summary statistics
  const totalInvoices = invoices.length;
  const totalAmount = invoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
  const paidAmount = invoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
  const pendingAmount = invoices.filter(inv => ['sent', 'overdue'].includes(inv.status)).reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
  const overdueCount = invoices.filter(inv => inv.status === 'overdue').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <SafeIcon icon={FiRefreshCw} className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading invoices...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col lg:flex-row lg:items-center lg:justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Invoice Management</h1>
          <p className="text-gray-600">
            Create, manage, and track professional invoices with GST compliance
          </p>
        </div>
        
        <div className="flex items-center space-x-3 mt-4 lg:mt-0">
          <button
            onClick={refetch}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
          >
            <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          
          <button
            onClick={() => {
              setSelectedInvoice(null);
              setShowModal(true);
            }}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            <span>Create Invoice</span>
          </button>
        </div>
      </motion.div>

      {/* Summary Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        <div className="bg-white rounded-xl p-6 shadow-soft border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Invoices</p>
              <p className="text-2xl font-bold text-gray-800">{totalInvoices}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiFileText} className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-soft border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Amount</p>
              <p className="text-2xl font-bold text-gray-800">{formatCurrency(totalAmount)}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiDollarSign} className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-soft border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Paid Amount</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(paidAmount)}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiIcons.FiCheckCircle} className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-soft border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Amount</p>
              <p className="text-2xl font-bold text-orange-600">{formatCurrency(pendingAmount)}</p>
              {overdueCount > 0 && (
                <p className="text-xs text-red-600">{overdueCount} overdue</p>
              )}
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiIcons.FiClock} className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Search, Filter, and Controls */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="relative flex-1 min-w-80">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search invoices by number, client, or business name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <select
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Invoices ({totalInvoices})</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="recurring">Recurring</option>
              <option value="one_time">One-Time</option>
              <option value="ad_spend">Ad Spend</option>
            </select>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="issue_date">Sort by Issue Date</option>
              <option value="due_date">Sort by Due Date</option>
              <option value="invoice_number">Sort by Invoice #</option>
              <option value="client">Sort by Client</option>
              <option value="total_amount">Sort by Amount</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
              title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
            >
              <SafeIcon icon={sortOrder === 'asc' ? FiArrowUp : FiArrowDown} className="w-4 h-4" />
            </button>
            
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-colors duration-200 ${
                viewMode === 'grid' ? 'bg-blue-100 text-blue-600 border border-blue-200' : 'hover:bg-gray-100 border border-gray-300'
              }`}
            >
              <SafeIcon icon={FiGrid} className="w-4 h-4" />
            </button>
            
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-colors duration-200 ${
                viewMode === 'list' ? 'bg-blue-100 text-blue-600 border border-blue-200' : 'hover:bg-gray-100 border border-gray-300'
              }`}
            >
              <SafeIcon icon={FiList} className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Showing {filteredInvoices.length} of {totalInvoices} invoices
          </p>
          
          {error && (
            <div className="text-red-600 text-sm">
              {error} (Running in demo mode)
            </div>
          )}
        </div>
      </motion.div>

      {/* Invoices Display */}
      {filteredInvoices.length > 0 ? (
        <div className={viewMode === 'grid' ? 
          'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6' : 
          'space-y-4'
        }>
          {filteredInvoices.map((invoice, index) => (
            <motion.div
              key={invoice.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className={`bg-white rounded-xl p-6 shadow-soft hover:shadow-medium transition-all duration-300 border border-gray-200 ${
                viewMode === 'list' ? 'flex items-center justify-between' : ''
              }`}
            >
              <div className={`${viewMode === 'list' ? 'flex-1' : ''}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-800 mb-1">{invoice.invoice_number}</h3>
                    <p className="text-gray-600 mb-2">{invoice.client?.business_name}</p>
                    <div className="flex items-center space-x-2 flex-wrap gap-1">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(invoice.status)}`}>
                        {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getTypeColor(invoice.invoice_type)}`}>
                        {invoice.invoice_type.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                  </div>
                  
                  {viewMode === 'grid' && (
                    <div className="flex items-center space-x-1 ml-2">
                      <button 
                        onClick={() => handleViewInvoice(invoice)}
                        className="p-2 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                        title="View Invoice"
                      >
                        <SafeIcon icon={FiEye} className="w-4 h-4 text-blue-600" />
                      </button>
                      <button 
                        onClick={() => handleEditInvoice(invoice)}
                        className="p-2 hover:bg-green-50 rounded-lg transition-colors duration-200"
                        title="Edit Invoice"
                      >
                        <SafeIcon icon={FiEdit2} className="w-4 h-4 text-green-600" />
                      </button>
                      <button 
                        onClick={() => handleDeleteInvoice(invoice.id)}
                        className="p-2 hover:bg-red-50 rounded-lg transition-colors duration-200"
                        title="Delete Invoice"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiCalendar} className="w-4 h-4 text-gray-400" />
                    <span>Issue: {formatDate(invoice.issue_date)}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiCalendar} className="w-4 h-4 text-gray-400" />
                    <span>Due: {formatDate(invoice.due_date)}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiDollarSign} className="w-4 h-4 text-gray-400" />
                    <span className="font-medium">{formatCurrency(invoice.total_amount)}</span>
                  </div>
                </div>

                {viewMode === 'grid' && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <select
                        value={invoice.status}
                        onChange={(e) => handleStatusChange(invoice.id, e.target.value)}
                        className="text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="draft">Draft</option>
                        <option value="sent">Sent</option>
                        <option value="paid">Paid</option>
                        <option value="overdue">Overdue</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                      
                      <div className="flex items-center space-x-1">
                        <button
                          className="p-1 hover:bg-gray-100 rounded transition-colors duration-200"
                          title="Send Invoice"
                        >
                          <SafeIcon icon={FiSend} className="w-3 h-3 text-gray-600" />
                        </button>
                        <button
                          className="p-1 hover:bg-gray-100 rounded transition-colors duration-200"
                          title="Download PDF"
                        >
                          <SafeIcon icon={FiDownload} className="w-3 h-3 text-gray-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {viewMode === 'list' && (
                <div className="flex items-center space-x-4 ml-6">
                  <div className="text-right min-w-24">
                    <p className="text-lg font-bold text-gray-800">{formatCurrency(invoice.total_amount)}</p>
                    <p className="text-xs text-gray-500">Due: {formatDate(invoice.due_date)}</p>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button 
                      onClick={() => handleViewInvoice(invoice)}
                      className="p-2 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                      title="View Invoice"
                    >
                      <SafeIcon icon={FiEye} className="w-4 h-4 text-blue-600" />
                    </button>
                    <button 
                      onClick={() => handleEditInvoice(invoice)}
                      className="p-2 hover:bg-green-50 rounded-lg transition-colors duration-200"
                      title="Edit Invoice"
                    >
                      <SafeIcon icon={FiEdit2} className="w-4 h-4 text-green-600" />
                    </button>
                    <button 
                      onClick={() => handleDeleteInvoice(invoice.id)}
                      className="p-2 hover:bg-red-50 rounded-lg transition-colors duration-200"
                      title="Delete Invoice"
                    >
                      <SafeIcon icon={FiTrash2} className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-12 shadow-soft text-center border border-gray-200"
        >
          <SafeIcon icon={FiFileText} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            {searchTerm || filterBy !== 'all' ? 'No invoices found' : 'No invoices yet'}
          </h3>
          <p className="text-gray-600 mb-6">
            {searchTerm || filterBy !== 'all' 
              ? 'Try adjusting your search or filter criteria'
              : 'Start by creating your first invoice to manage your billing'
            }
          </p>
          {(!searchTerm && filterBy === 'all') && (
            <button
              onClick={() => {
                setSelectedInvoice(null);
                setShowModal(true);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors duration-200"
            >
              Create Your First Invoice
            </button>
          )}
        </motion.div>
      )}

      {/* Modals */}
      <InvoiceModal 
        isOpen={showModal} 
        onClose={() => {
          setShowModal(false);
          setSelectedInvoice(null);
        }}
        invoice={selectedInvoice}
        onSubmit={selectedInvoice ? handleUpdateInvoice : handleCreateInvoice}
      />

      <InvoicePreview
        isOpen={showPreview}
        onClose={() => {
          setShowPreview(false);
          setSelectedInvoice(null);
        }}
        invoice={selectedInvoice}
      />
    </div>
  );
};

export default Invoices;